import { ref } from "vue";
import type { InitialRegistrationData } from "@/app/registration-app/user/invited-registration/@types/InitialRegistrationData";
import type { Message } from "@/app/registration-app/user/invited-registration/@types/Message";
import { getRegistrationData } from "@/app/registration-app/user/invited-registration/services/getRegistrationData.service";
import { useI18n } from "vue-i18n";
import {
  type RegistrationType,
  RegistrationSalutationInput,
} from "@/shared/generated/graphql";

const scopeByErrorKey: Record<string, string | undefined> = {
  NO_VALID_PROVISIONING_KEY: "registration.user.invited.error.invalidLink",
  INVITATION_LINK_ALREADY_USED:
    "registration.user.invited.error.invitationLinkAlreadyUsed",
  GENERAL_ERROR: "shared.error.page.generalError",
};

const isRegistrationSalutationInput = (
  value: unknown,
): value is RegistrationSalutationInput => {
  return Object.values(RegistrationSalutationInput).includes(
    value as RegistrationSalutationInput,
  );
};

export const useGetRegistrationData = (
  provisioningKey: string,
  registrationType: RegistrationType,
  showError: (message: Message) => void,
) => {
  const isFetchingRegistrationData = ref(false);
  const initialValues = ref<Partial<InitialRegistrationData>>({});
  const finishedRegistration = ref<boolean>(false);
  const { t } = useI18n();

  const onFetchingRegistrationData = async () => {
    try {
      isFetchingRegistrationData.value = true;
      const registrationData = await getRegistrationData(
        provisioningKey,
        registrationType,
      );
      if (registrationData) {
        finishedRegistration.value =
          registrationData.finishedRegistration ?? false;
        initialValues.value = {
          email: registrationData.email ?? undefined,
        };
        if (
          registrationData.salutation &&
          isRegistrationSalutationInput(registrationData.salutation)
        )
          initialValues.value.salutation = registrationData.salutation;
        if (registrationData.firstName)
          initialValues.value.firstName = registrationData.firstName;
        if (registrationData.lastName)
          initialValues.value.lastName = registrationData.lastName;
      }
    } catch (error) {
      const key = (error as string) ?? "GENERAL_ERROR";
      const scope = scopeByErrorKey[key] ?? scopeByErrorKey.GENERAL_ERROR;
      showError({ title: t(`${scope}.title`), text: t(`${scope}.text`) });
    } finally {
      isFetchingRegistrationData.value = false;
    }
  };

  return {
    isFetchingRegistrationData,
    onFetchingRegistrationData,
    initialValues,
    finishedRegistration,
  };
};
